/**
 * E018-S008-T004: Unified Related Cell Values Utility
 *
 * This utility provides a centralized way to fetch cell values from related rows,
 * handling both InDoc and CrossDoc scenarios. Rollups use this instead of
 * implementing their own CrossDoc logic.
 *
 * Principle: CrossDoc logic lives here, not in individual consumers (Rollups).
 */

import type { Store } from '@blocksuite/store';
import { signal } from '@preact/signals-core';

import { resolveRelationEndpointAccess } from './access-state';
import { getFormattedTargetCellValue } from './database-discovery';
import {
  databaseAccessRevision,
  getCrossDocCellValue,
  getDatabaseSchemaRevision,
} from './database-discovery-bridge';

// =============================================================================
// Cache
// =============================================================================

/**
 * Cache for CrossDoc cell values.
 * Key: `${targetDocId}:${targetDatabaseId}:${rowId}:${fieldId}`
 */
const crossDocValueCache = new Map<string, string>();

let cacheRevision = -1;
let cacheGeneration = 0;
const pendingValues = new Map<string, Promise<void>>();
const cachedSchemaRevisions = new Map<string, number>();

function syncAccessSnapshot(): void {
  const revision = databaseAccessRevision.value;
  if (cacheRevision === revision) return;
  cacheRevision = revision;
  cacheGeneration++;
  crossDocValueCache.clear();
  pendingValues.clear();
  cachedSchemaRevisions.clear();
}

/**
 * Signal that increments when cache is updated.
 * Used to trigger re-evaluation of computed values in consumers.
 */
export const relatedValuesCacheVersion = signal(0);

function getCacheKey(
  targetDocId: string,
  targetDatabaseId: string,
  rowId: string,
  fieldId: string
): string {
  return `${targetDocId}:${targetDatabaseId}:${rowId}:${fieldId}`;
}

// =============================================================================
// Value Formatting
// =============================================================================

interface SelectOption {
  id: string;
  value: string;
  color?: string;
}

/**
 * Format a raw value based on property type for display.
 */
function formatValueForDisplay(
  rawValue: unknown,
  propertyType?: string,
  propertyData?: Record<string, unknown>
): string {
  if (rawValue === null || rawValue === undefined) return '';

  // Handle Select/Multi-Select: Convert ID to label
  if (
    (propertyType === 'select' || propertyType === 'multi-select') &&
    propertyData
  ) {
    const options = propertyData.options as SelectOption[] | undefined;
    if (options && typeof rawValue === 'string') {
      const option = options.find(o => o.id === rawValue);
      return option?.value ?? String(rawValue);
    }
    if (options && Array.isArray(rawValue)) {
      return rawValue
        .map(id => {
          const option = options.find(o => o.id === id);
          return option?.value ?? String(id);
        })
        .join(', ');
    }
  }

  // Handle Rich-Text: Extract plain text
  if (
    (propertyType === 'rich-text' || propertyType === 'text') &&
    typeof rawValue === 'object' &&
    rawValue !== null &&
    'toString' in rawValue
  ) {
    return rawValue.toString();
  }

  // Handle Checkbox
  if (propertyType === 'checkbox') {
    return rawValue ? '✓' : '✗';
  }

  // Default conversions
  if (typeof rawValue === 'string') return rawValue;
  if (typeof rawValue === 'number') return String(rawValue);
  if (typeof rawValue === 'boolean') return rawValue ? '✓' : '✗';
  if (Array.isArray(rawValue)) return rawValue.map(String).join(', ');
  if (
    typeof rawValue === 'object' &&
    rawValue !== null &&
    'toString' in rawValue
  ) {
    return rawValue.toString();
  }

  return String(rawValue);
}

// =============================================================================
// Main API
// =============================================================================

/**
 * Info needed to load missing CrossDoc values asynchronously.
 */
export interface CrossDocLoadInfo {
  accessRevision: number;
  schemaRevision: number;
  cacheGeneration: number;
  targetDocId: string;
  targetDatabaseId: string;
  targetFieldId: string;
  rowIds: string[];
}

/**
 * Result of getRelatedCellValues.
 */
export interface RelatedCellValuesResult {
  state: 'available' | 'restricted' | 'missing' | 'trashed';
  /** Formatted values for display */
  values: string[];
  /** Whether some values are missing and need async loading */
  needsAsyncLoad: boolean;
  /** Info for async loading (only set if needsAsyncLoad is true) */
  loadInfo: CrossDocLoadInfo | null;
}

/**
 * Get formatted cell values from related rows.
 *
 * This is the main entry point for Rollups and other consumers.
 * Handles both InDoc and CrossDoc scenarios automatically.
 *
 * @param store - The current document's store
 * @param currentDocId - The current document's ID
 * @param relationData - The relation property data (targetDatabaseId, targetDocId)
 * @param rowIds - The related row IDs to fetch values for
 * @param targetFieldId - The field to get values from
 * @returns Formatted values and async load info if needed
 */
export function getRelatedCellValues(
  store: Store,
  currentDocId: string,
  relationData: {
    targetDatabaseId?: string;
    targetDocId?: string;
  },
  rowIds: string[],
  targetFieldId: string
): RelatedCellValuesResult {
  syncAccessSnapshot();
  const { targetDatabaseId } = relationData;
  const access = resolveRelationEndpointAccess(relationData);
  if (access.state !== 'write' && access.state !== 'read') {
    return {
      state: access.state,
      values: [],
      needsAsyncLoad: false,
      loadInfo: null,
    };
  }
  if (!targetDatabaseId || rowIds.length === 0) {
    return {
      state: 'available',
      values: [],
      needsAsyncLoad: false,
      loadInfo: null,
    };
  }
  // Follow the live host, including databases moved since the relation was configured.
  if (access.targetDocId && access.targetDocId !== currentDocId) {
    return getCrossDocValues(
      access.targetDocId,
      targetDatabaseId,
      rowIds,
      targetFieldId
    );
  }

  // InDoc: Get values directly
  const values: string[] = [];
  for (const rowId of rowIds) {
    const value = getFormattedTargetCellValue(
      store,
      targetDatabaseId,
      rowId,
      targetFieldId
    );
    values.push(value);
  }

  return { state: 'available', values, needsAsyncLoad: false, loadInfo: null };
}

/**
 * Get CrossDoc values from cache, marking missing ones for async load.
 */
function getCrossDocValues(
  targetDocId: string,
  targetDatabaseId: string,
  rowIds: string[],
  targetFieldId: string
): RelatedCellValuesResult {
  const schemaRevision = getDatabaseSchemaRevision(
    targetDocId,
    targetDatabaseId
  );
  const prefix = `${targetDocId}:${targetDatabaseId}:`;
  if (cachedSchemaRevisions.get(prefix) !== schemaRevision) {
    for (const key of crossDocValueCache.keys()) {
      if (key.startsWith(prefix)) crossDocValueCache.delete(key);
    }
    for (const key of pendingValues.keys()) {
      if (key.startsWith(prefix)) pendingValues.delete(key);
    }
    cachedSchemaRevisions.set(prefix, schemaRevision);
  }
  const values: string[] = [];
  let hasMissingValues = false;

  for (const rowId of rowIds) {
    const cacheKey = getCacheKey(
      targetDocId,
      targetDatabaseId,
      rowId,
      targetFieldId
    );
    const cached = crossDocValueCache.get(cacheKey);

    if (cached !== undefined) {
      values.push(cached);
    } else {
      hasMissingValues = true;
    }
  }

  return {
    state: 'available',
    values,
    needsAsyncLoad: hasMissingValues,
    loadInfo: hasMissingValues
      ? {
          accessRevision: databaseAccessRevision.value,
          schemaRevision,
          cacheGeneration,
          targetDocId,
          targetDatabaseId,
          targetFieldId,
          rowIds,
        }
      : null,
  };
}

/**
 * Load missing CrossDoc values asynchronously.
 *
 * Call this from a useEffect when loadInfo is set.
 * After loading, the cache is updated and cacheVersion is incremented,
 * which will trigger re-evaluation of computed signals.
 *
 * @param loadInfo - The load info from getRelatedCellValues
 * @returns Promise that resolves when loading is complete
 */
export async function loadRelatedCellValuesAsync(
  loadInfo: CrossDocLoadInfo
): Promise<void> {
  syncAccessSnapshot();
  const { targetDocId, targetDatabaseId, targetFieldId, rowIds } = loadInfo;
  const isCurrent = () => {
    if (
      loadInfo.accessRevision !== databaseAccessRevision.value ||
      loadInfo.schemaRevision !==
        getDatabaseSchemaRevision(targetDocId, targetDatabaseId) ||
      loadInfo.cacheGeneration !== cacheGeneration
    )
      return false;
    const access = resolveRelationEndpointAccess(loadInfo);
    return (
      (access.state === 'read' || access.state === 'write') &&
      access.targetDocId === targetDocId
    );
  };
  if (!isCurrent()) return;
  let changed = false;
  for (const rowId of rowIds) {
    if (!isCurrent()) return;
    const cacheKey = getCacheKey(
      targetDocId,
      targetDatabaseId,
      rowId,
      targetFieldId
    );
    if (crossDocValueCache.has(cacheKey)) continue;
    let pending = pendingValues.get(cacheKey);
    if (!pending) {
      pending = (async () => {
        const result = await getCrossDocCellValue(
          targetDocId,
          targetDatabaseId,
          rowId,
          targetFieldId
        );
        // Neither a revoked permission nor a replaced workspace may repopulate the cache.
        if (!isCurrent() || !result.success || result.value === undefined)
          return;
        const rawValue =
          Array.isArray(result.value) && result.value.length === 1
            ? result.value[0]
            : result.value;
        crossDocValueCache.set(
          cacheKey,
          formatValueForDisplay(
            rawValue,
            result.propertyType,
            result.propertyData
          )
        );
        changed = true;
      })();
      pendingValues.set(cacheKey, pending);
    }
    try {
      await pending;
    } finally {
      if (pendingValues.get(cacheKey) === pending)
        pendingValues.delete(cacheKey);
    }
  }
  if (changed && isCurrent()) relatedValuesCacheVersion.value++;
}

/**
 * Clear all caches. Call this when navigating away or when data might be stale.
 */
export function clearRelatedCellValuesCache(): void {
  crossDocValueCache.clear();
  pendingValues.clear();
  cachedSchemaRevisions.clear();
  cacheGeneration++;
  relatedValuesCacheVersion.value++;
}
